import { Injectable } from '@angular/core';
import { Subject, Observable } from 'rxjs';
import { WebSocketService } from './websocket.service';

/**
 * Event types from backend
 */
export interface PayoutRequestEvent {
  type: 'requested' | 'approved' | 'rejected' | 'cancelled' | 'completed' | 'payment_confirmed' | 'payment_failed';
  data: {
    id: number;
    driverId?: number;
    companyId?: number;
    amount: number;
    status: string;
    oldStatus: string | null;
    processedAt: string | null;
    processedBy?: number | null;
    rejectionReason: string | null;
    notes: string | null;
    walletTransactionId?: number | null;
    // Payment-specific fields
    paymentId?: number;
    orderId?: number;
    error?: string;
    errorCode?: string;
  };
}

/**
 * Payout WebSocket Service
 * 
 * Listens to payout request events from WebSocket
 * and provides typed observables for components
 */
@Injectable({ providedIn: 'root' })
export class PayoutWebSocketService {
  private payoutEvents$ = new Subject<PayoutRequestEvent>();

  constructor(private websocket: WebSocketService) {
    // Subscribe immediately
    this.subscribeToEvents();
  }

  /**
   * Subscribe to WebSocket events
   * @private
   */
  private subscribeToEvents(): void {
    
    // Company channel events (for admins/dispatchers)
    this.websocket.getEventsByType$('payout-payout-requested').subscribe((data: unknown) => {
      this.payoutEvents$.next({ type: 'requested', data: data as PayoutRequestEvent['data'] });
    });

    this.websocket.getEventsByType$('payout-payout-approved').subscribe((data: unknown) => {
      this.payoutEvents$.next({ type: 'approved', data: data as PayoutRequestEvent['data'] });
    });

    this.websocket.getEventsByType$('payout-payout-rejected').subscribe((data: unknown) => {
      this.payoutEvents$.next({ type: 'rejected', data: data as PayoutRequestEvent['data'] });
    });

    this.websocket.getEventsByType$('payout-payout-cancelled').subscribe((data: unknown) => {
      this.payoutEvents$.next({ type: 'cancelled', data: data as PayoutRequestEvent['data'] });
    });

    this.websocket.getEventsByType$('payout-payout-completed').subscribe((data: unknown) => {
      this.payoutEvents$.next({ type: 'completed', data: data as PayoutRequestEvent['data'] });
    });

    // WSS-003: payment-confirmed event — fires when a payment linked to a payout is confirmed
    // Backend: entityType=payment, action=payment-success → event 'payment-payment-success'
    this.websocket.getEventsByType$('payment-payment-success').subscribe((data: unknown) => {
      this.payoutEvents$.next({ type: 'payment_confirmed', data: data as PayoutRequestEvent['data'] });
    });

    // WSS-003: payment-failed event — fires when a payment linked to a payout fails
    // Backend: entityType=payment, action=payment-failed → event 'payment-payment-failed'
    this.websocket.getEventsByType$('payment-payment-failed').subscribe((data: unknown) => {
      this.payoutEvents$.next({ type: 'payment_failed', data: data as PayoutRequestEvent['data'] });
    });

    // Also handle dispatcher-specific payment-failed (company channel)
    this.websocket.getEventsByType$('payment-dispatcher-payment-failed').subscribe((data: unknown) => {
      this.payoutEvents$.next({ type: 'payment_failed', data: data as PayoutRequestEvent['data'] });
    });
  }

  /**
   * Get observable for payout events
   * Components subscribe to this
   */
  getPayoutEvents$(): Observable<PayoutRequestEvent> {
    return this.payoutEvents$.asObservable();
  }
}
